import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';
import OrganizationEmptyState from '../components/OrganizationEmptyState';

function formatCreatorName(row) {
  return row.created_by_name || row.created_by_user_id || 'Unknown';
}

function validateDob(value) {
  const normalized = (value || '').trim();
  if (!normalized) {
    return 'DOB is required.';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return 'Use YYYY-MM-DD.';
  }
  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return 'Enter a valid DOB.';
  }
  const [year, month, day] = normalized.split('-').map(Number);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return 'Enter a valid DOB.';
  }
  const now = new Date();
  if (date > now) {
    return 'DOB cannot be in the future.';
  }
  return '';
}

function formatPracticeAddress(practice) {
  if (!practice) {
    return 'No address';
  }
  const line1 = [practice.street_1, practice.street_2].filter(Boolean).join(', ');
  const line2 = [practice.city, practice.state].filter(Boolean).join(', ');
  const zip = practice.zip_code || '';
  return [line1, [line2, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || 'No address';
}

function TwoDBarcodePlaceholder() {
  return (
    <Box
      aria-label="2D barcode placeholder"
      sx={{
        border: '1px solid #ccd6dc',
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 104,
        minHeight: 104,
      }}
    >
      <QrCode2Icon sx={{ fontSize: 56, color: '#334155' }} />
    </Box>
  );
}

export default function LabAccessioning() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const navigate = useNavigate();
  const { tokens } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const accessionWizardSteps = ['Scan Sample', 'Enter Order Details', 'Confirm Order', 'Inspect Sample', 'Final Directions'];

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [accessionRefreshNonce, setAccessionRefreshNonce] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [barcodeMatchFilter, setBarcodeMatchFilter] = useState('all');
  const [hasCaseFilter, setHasCaseFilter] = useState('all');
  const [newAccessionDialogOpen, setNewAccessionDialogOpen] = useState(false);
  const [newAccessionStep, setNewAccessionStep] = useState(0);
  const [newAccessionRequisitionBarcode, setNewAccessionRequisitionBarcode] = useState('');
  const [newAccessionSampleBarcode, setNewAccessionSampleBarcode] = useState('');
  const [newAccessionDobTouched, setNewAccessionDobTouched] = useState(false);
  const [skuSearchInput, setSkuSearchInput] = useState('');
  const [skuSearchText, setSkuSearchText] = useState('');
  const [skuSearchResults, setSkuSearchResults] = useState([]);
  const [skuSearchLoading, setSkuSearchLoading] = useState(false);
  const [skuSearchError, setSkuSearchError] = useState(null);
  const [selectedSkuOption, setSelectedSkuOption] = useState(null);
  const [skuSearchAnchorEl, setSkuSearchAnchorEl] = useState(null);
  const [workflowQuestions, setWorkflowQuestions] = useState([]);
  const [workflowQuestionsLoading, setWorkflowQuestionsLoading] = useState(false);
  const [workflowQuestionsError, setWorkflowQuestionsError] = useState(null);
  const [workflowQuestionAnswers, setWorkflowQuestionAnswers] = useState({});
  const [rejectSampleDialogOpen, setRejectSampleDialogOpen] = useState(false);
  const [saveAccessionLoading, setSaveAccessionLoading] = useState(false);
  const [saveAccessionError, setSaveAccessionError] = useState(null);
  const [advanceStepLoading, setAdvanceStepLoading] = useState(false);
  const [advanceStepError, setAdvanceStepError] = useState(null);
  const [practiceSearchInput, setPracticeSearchInput] = useState('');
  const [practiceSearchText, setPracticeSearchText] = useState('');
  const [practiceSearchResults, setPracticeSearchResults] = useState([]);
  const [practiceSearchLoading, setPracticeSearchLoading] = useState(false);
  const [practiceSearchError, setPracticeSearchError] = useState(null);
  const [selectedPracticeOption, setSelectedPracticeOption] = useState(null);
  const [practiceSearchAnchorEl, setPracticeSearchAnchorEl] = useState(null);
  const [providerSearchInput, setProviderSearchInput] = useState('');
  const [providerSearchText, setProviderSearchText] = useState('');
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);
  const [providerSearchError, setProviderSearchError] = useState(null);
  const [selectedProviderOption, setSelectedProviderOption] = useState(null);
  const [providerSearchAnchorEl, setProviderSearchAnchorEl] = useState(null);
  const [newAccessionOrderDetails, setNewAccessionOrderDetails] = useState({
    patient_first_name: '',
    patient_last_name: '',
    patient_dob: '',
    sku_number: '',
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchText(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSkuSearchText(skuSearchInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [skuSearchInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPracticeSearchText(practiceSearchInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [practiceSearchInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProviderSearchText(providerSearchInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [providerSearchInput]);

  useEffect(() => {
    setPage(0);
  }, [selectedPractice?.id]);

  useEffect(() => {
    if (!tokens?.id_token || !selectedPractice?.id) {
      setRows([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      skip: String(page * rowsPerPage),
      limit: String(rowsPerPage),
      barcode_match_filter: barcodeMatchFilter,
      has_case_filter: hasCaseFilter,
    });
    if (statusFilter !== 'all') {
      params.append('status', statusFilter);
    }
    if (searchText) {
      params.append('search_text', searchText);
    }

    setLoading(true);
    setError(null);

    fetch(`${apiBase}/lab/accessions?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load accessions');
        }
        return response.json();
      })
      .then((data) => {
        setRows(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load accessions');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [
    accessionRefreshNonce,
    apiBase,
    barcodeMatchFilter,
    hasCaseFilter,
    page,
    rowsPerPage,
    searchText,
    selectedPractice?.id,
    statusFilter,
    tokens?.id_token,
  ]);

  useEffect(() => {
    if (!newAccessionDialogOpen || !tokens?.id_token || !selectedPractice?.id) {
      setSkuSearchResults([]);
      setSkuSearchLoading(false);
      setSkuSearchError(null);
      return;
    }
    if (!skuSearchText.trim()) {
      setSkuSearchResults([]);
      setSkuSearchLoading(false);
      setSkuSearchError(null);
      return;
    }
    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      limit: '5',
    });
    params.append('search_text', skuSearchText.trim());
    setSkuSearchLoading(true);
    setSkuSearchError(null);
    fetch(`${apiBase}/lab/accessioning/lab-test-skus?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load lab test SKUs');
        }
        return response.json();
      })
      .then((data) => {
        setSkuSearchResults(data.items || []);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setSkuSearchError(err.message || 'Failed to load lab test SKUs');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSkuSearchLoading(false);
        }
      });
    return () => controller.abort();
  }, [apiBase, newAccessionDialogOpen, selectedPractice?.id, skuSearchText, tokens?.id_token]);

  useEffect(() => {
    if (!newAccessionDialogOpen || !tokens?.id_token || !selectedSkuOption?.lab_test_workflow_id) {
      setWorkflowQuestions([]);
      setWorkflowQuestionsLoading(false);
      setWorkflowQuestionsError(null);
      setWorkflowQuestionAnswers({});
      return;
    }
    const controller = new AbortController();
    setWorkflowQuestionsLoading(true);
    setWorkflowQuestionsError(null);
    fetch(`${apiBase}/lab/accessioning/lab-test-workflows/${selectedSkuOption.lab_test_workflow_id}`, {
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load workflow questions');
        }
        return response.json();
      })
      .then((data) => {
        const activeQuestions = (data.questions || [])
          .filter((question) => question.is_active)
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        setWorkflowQuestions(activeQuestions);
        setWorkflowQuestionAnswers((current) => {
          const next = {};
          activeQuestions.forEach((question) => {
            next[question.id] = current[question.id] || 'na';
          });
          return next;
        });
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setWorkflowQuestions([]);
          setWorkflowQuestionAnswers({});
          setWorkflowQuestionsError(err.message || 'Failed to load workflow questions');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setWorkflowQuestionsLoading(false);
        }
      });
    return () => controller.abort();
  }, [apiBase, newAccessionDialogOpen, selectedSkuOption?.lab_test_workflow_id, tokens?.id_token]);

  useEffect(() => {
    if (!newAccessionDialogOpen || newAccessionStep < 1 || !tokens?.id_token || !selectedPractice?.id) {
      setPracticeSearchResults([]);
      setPracticeSearchLoading(false);
      setPracticeSearchError(null);
      return;
    }
    if (!practiceSearchText.trim()) {
      setPracticeSearchResults([]);
      setPracticeSearchLoading(false);
      setPracticeSearchError(null);
      return;
    }
    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      limit: '5',
    });
    params.append('search_text', practiceSearchText.trim());
    setPracticeSearchLoading(true);
    setPracticeSearchError(null);
    fetch(`${apiBase}/lab/practices/search?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load practices');
        }
        return response.json();
      })
      .then((data) => {
        setPracticeSearchResults(data.items || []);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setPracticeSearchError(err.message || 'Failed to load practices');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setPracticeSearchLoading(false);
        }
      });
    return () => controller.abort();
  }, [apiBase, newAccessionDialogOpen, newAccessionStep, practiceSearchText, selectedPractice?.id, tokens?.id_token]);

  useEffect(() => {
    if (!selectedPracticeOption) {
      setProviderSearchResults([]);
      setProviderSearchLoading(false);
      setProviderSearchError(null);
      return;
    }
    if (!newAccessionDialogOpen || newAccessionStep < 1 || !tokens?.id_token || !selectedPractice?.id) {
      return;
    }
    if (!providerSearchText.trim()) {
      setProviderSearchResults([]);
      setProviderSearchLoading(false);
      setProviderSearchError(null);
      return;
    }
    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      limit: '5',
    });
    params.append('search_text', providerSearchText.trim());
    setProviderSearchLoading(true);
    setProviderSearchError(null);
    fetch(`${apiBase}/lab/practices/${selectedPracticeOption.id}/providers?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load providers');
        }
        return response.json();
      })
      .then((data) => {
        setProviderSearchResults(data.items || []);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setProviderSearchError(err.message || 'Failed to load providers');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setProviderSearchLoading(false);
        }
      });
    return () => controller.abort();
  }, [
    apiBase,
    newAccessionDialogOpen,
    newAccessionStep,
    providerSearchText,
    selectedPractice?.id,
    selectedPracticeOption,
    tokens?.id_token,
  ]);

  const statusOptions = useMemo(() => {
    const values = new Set(rows.map((row) => row.status).filter(Boolean));
    if (statusFilter !== 'all') {
      values.add(statusFilter);
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [rows, statusFilter]);

  const hasBothWizardScans =
    newAccessionRequisitionBarcode.trim().length > 0 && newAccessionSampleBarcode.trim().length > 0;
  const wizardBarcodesMatch =
    hasBothWizardScans &&
    newAccessionRequisitionBarcode.trim() === newAccessionSampleBarcode.trim();
  const wizardSampleId = wizardBarcodesMatch ? newAccessionSampleBarcode.trim() : '';
  const dobError = validateDob(newAccessionOrderDetails.patient_dob);
  const wizardHasOrderDetails = Boolean(
    newAccessionOrderDetails.patient_first_name.trim() &&
    newAccessionOrderDetails.patient_last_name.trim() &&
    !dobError &&
    selectedSkuOption &&
    selectedPracticeOption &&
    selectedProviderOption
  );
  const workflowQuestionsComplete =
    workflowQuestions.length === 0 ||
    workflowQuestions.every((question) => workflowQuestionAnswers[question.id] && workflowQuestionAnswers[question.id] !== 'na');
  const workflowQuestionsAllYes =
    workflowQuestions.length === 0 ||
    workflowQuestions.every((question) => workflowQuestionAnswers[question.id] === 'yes');
  const negativeWorkflowQuestions = workflowQuestions
    .filter((question) => workflowQuestionAnswers[question.id] === 'no')
    .map((question) => ({
      id: question.id,
      summary: `${question.question_text}: No`,
    }));
  const showRejectSampleButton = workflowQuestionsComplete && negativeWorkflowQuestions.length > 0;
  const workflowQuestionSummaries = workflowQuestions
    .filter((question) => workflowQuestionAnswers[question.id] && workflowQuestionAnswers[question.id] !== 'na')
    .map((question) => ({
      id: question.id,
      question_text: question.question_text,
      answer:
        workflowQuestionAnswers[question.id] === 'yes'
          ? 'Yes'
          : workflowQuestionAnswers[question.id] === 'no'
            ? 'No'
            : '-',
    }));
  const skuPopoverOpen = Boolean(skuSearchAnchorEl && skuSearchInput.trim());
  const practicePopoverOpen = Boolean(practiceSearchAnchorEl && practiceSearchInput.trim());
  const providerPopoverOpen = Boolean(providerSearchAnchorEl && providerSearchInput.trim() && selectedPracticeOption);
  const segmentGroupSx = {
    width: 210,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    '& .MuiToggleButton-root': {
      flex: 1,
      minWidth: 0,
      px: 0,
    },
  };

  const handleOpenNewAccessionDialog = () => {
    setNewAccessionStep(0);
    setNewAccessionRequisitionBarcode('');
    setNewAccessionSampleBarcode('');
    setNewAccessionDobTouched(false);
    setSkuSearchInput('');
    setSkuSearchText('');
    setSkuSearchResults([]);
    setSkuSearchError(null);
    setSelectedSkuOption(null);
    setSkuSearchAnchorEl(null);
    setWorkflowQuestions([]);
    setWorkflowQuestionsError(null);
    setWorkflowQuestionAnswers({});
    setRejectSampleDialogOpen(false);
    setSaveAccessionLoading(false);
    setSaveAccessionError(null);
    setAdvanceStepLoading(false);
    setAdvanceStepError(null);
    setPracticeSearchInput('');
    setPracticeSearchText('');
    setPracticeSearchResults([]);
    setPracticeSearchError(null);
    setSelectedPracticeOption(null);
    setPracticeSearchAnchorEl(null);
    setProviderSearchInput('');
    setProviderSearchText('');
    setProviderSearchResults([]);
    setProviderSearchError(null);
    setSelectedProviderOption(null);
    setProviderSearchAnchorEl(null);
    setNewAccessionOrderDetails({
      patient_first_name: '',
      patient_last_name: '',
      patient_dob: '',
      sku_number: '',
    });
    setNewAccessionDialogOpen(true);
  };

  const handleCloseNewAccessionDialog = () => {
    setNewAccessionDialogOpen(false);
    setNewAccessionStep(0);
    setNewAccessionRequisitionBarcode('');
    setNewAccessionSampleBarcode('');
    setNewAccessionDobTouched(false);
    setSkuSearchInput('');
    setSkuSearchText('');
    setSkuSearchResults([]);
    setSkuSearchError(null);
    setSelectedSkuOption(null);
    setSkuSearchAnchorEl(null);
    setWorkflowQuestions([]);
    setWorkflowQuestionsError(null);
    setWorkflowQuestionAnswers({});
    setRejectSampleDialogOpen(false);
    setSaveAccessionLoading(false);
    setSaveAccessionError(null);
    setAdvanceStepLoading(false);
    setAdvanceStepError(null);
    setPracticeSearchInput('');
    setPracticeSearchText('');
    setPracticeSearchResults([]);
    setPracticeSearchError(null);
    setSelectedPracticeOption(null);
    setPracticeSearchAnchorEl(null);
    setProviderSearchInput('');
    setProviderSearchText('');
    setProviderSearchResults([]);
    setProviderSearchError(null);
    setSelectedProviderOption(null);
    setProviderSearchAnchorEl(null);
    setNewAccessionOrderDetails({
      patient_first_name: '',
      patient_last_name: '',
      patient_dob: '',
      sku_number: '',
    });
  };

  const handleNewAccessionOrderDetailChange = (field, value) => {
    setNewAccessionOrderDetails((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSelectSkuOption = (sku) => {
    setSelectedSkuOption(sku);
    setSkuSearchInput('');
    setSkuSearchText('');
    setSkuSearchAnchorEl(null);
    setWorkflowQuestionsError(null);
    setWorkflowQuestionAnswers({});
    setSaveAccessionError(null);
    setAdvanceStepError(null);
    handleNewAccessionOrderDetailChange('sku_number', sku?.sku_number || '');
  };

  const handleSelectPracticeOption = (practice) => {
    setSelectedPracticeOption(practice);
    setPracticeSearchInput('');
    setPracticeSearchText('');
    setPracticeSearchAnchorEl(null);
    setSelectedProviderOption(null);
    setProviderSearchInput('');
    setProviderSearchText('');
    setProviderSearchResults([]);
    setProviderSearchError(null);
    setProviderSearchAnchorEl(null);
  };

  const handleSelectProviderOption = (provider) => {
    setSelectedProviderOption(provider);
    setProviderSearchInput('');
    setProviderSearchText('');
    setProviderSearchAnchorEl(null);
  };

  const buildAccessionPayload = (isRejected = false) => ({
    organization_id: selectedPractice.id,
    lab_test_workflow_id: selectedSkuOption.lab_test_workflow_id,
    lab_test_sku_id: selectedSkuOption.id || null,
    practice_organization_id: selectedPracticeOption?.id || null,
    provider_user_id: selectedProviderOption?.user_id || null,
    requisition_barcode: newAccessionRequisitionBarcode.trim() || null,
    sample_barcode: newAccessionSampleBarcode.trim() || null,
    sample_id: wizardSampleId || null,
    status: 'New',
    patient_first_name: newAccessionOrderDetails.patient_first_name.trim() || null,
    patient_last_name: newAccessionOrderDetails.patient_last_name.trim() || null,
    patient_dob: newAccessionOrderDetails.patient_dob.trim() || null,
    provider_name: [selectedProviderOption?.first_name, selectedProviderOption?.last_name].filter(Boolean).join(' ') || null,
    practice_npi: selectedPracticeOption?.npi || null,
    provider_address: formatPracticeAddress(selectedPracticeOption),
    practice_name: selectedPracticeOption?.name || null,
    is_rejected: isRejected,
    answers: workflowQuestions
      .filter((question) => workflowQuestionAnswers[question.id] && workflowQuestionAnswers[question.id] !== 'na')
      .map((question) => ({
        question_id: question.id,
        answer: workflowQuestionAnswers[question.id] === 'yes' ? 'Yes' : 'No',
      })),
  });

  const saveAccession = (isRejected = false) => {
    if (!tokens?.id_token || !selectedPractice?.id || !selectedSkuOption?.lab_test_workflow_id) {
      setSaveAccessionError('Select a SKU with an assigned workflow before saving.');
      return;
    }
    setSaveAccessionLoading(true);
    setSaveAccessionError(null);
    fetch(`${apiBase}/lab/accessions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(buildAccessionPayload(isRejected)),
    })
      .then(async (response) => {
        if (!response.ok) {
          let message = 'Failed to save accession';
          try {
            const data = await response.json();
            if (data?.detail) {
              message = data.detail;
            }
          } catch (error) {
            // Keep fallback message when the error response is not JSON.
          }
          throw new Error(message);
        }
        return response.json();
      })
      .then(() => {
        setPage(0);
        setAccessionRefreshNonce((value) => value + 1);
        handleCloseNewAccessionDialog();
      })
      .catch((error) => {
        setSaveAccessionError(error.message || 'Failed to save accession');
      })
      .finally(() => {
        setSaveAccessionLoading(false);
      });
  };

  const handleConfirmRejectSample = () => {
    saveAccession(true);
  };

  const handleSaveAccession = () => {
    saveAccession(false);
  };

  const handleContinueAccessionWizard = async () => {
    if (newAccessionStep !== 0) {
      setAdvanceStepError(null);
      setNewAccessionStep((value) => value + 1);
      return;
    }

    if (!tokens?.id_token || !selectedPractice?.id || !wizardSampleId) {
      setNewAccessionStep((value) => value + 1);
      return;
    }

    setAdvanceStepLoading(true);
    setAdvanceStepError(null);

    try {
      const params = new URLSearchParams({
        organization_id: selectedPractice.id,
        skip: '0',
        limit: '1',
        barcode_match_filter: 'all',
        has_case_filter: 'all',
        exact_sample_id: wizardSampleId,
      });
      const response = await fetch(`${apiBase}/lab/accessions?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${tokens.id_token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to validate sample ID');
      }
      const data = await response.json();
      const duplicate = Number(data.total || 0) > 0 || (data.items || []).length > 0;
      if (duplicate) {
        setAdvanceStepError('This sample ID is already in use. Scan a new unique sample barcode before continuing.');
        return;
      }
      setNewAccessionStep((value) => value + 1);
    } catch (error) {
      setAdvanceStepError(error.message || 'Failed to validate sample ID');
    } finally {
      setAdvanceStepLoading(false);
    }
  };

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      {!selectedPractice && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a lab to view accessions.
        </Typography>
      )}

      {!selectedPractice ? (
        <OrganizationEmptyState label="lab" />
      ) : (
        <Card>
          <CardContent>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Track accessions for the selected lab organization.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenNewAccessionDialog}
              >
                New Accession
              </Button>
            </Box>

            <Box sx={{ mb: 2, overflowX: 'auto' }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  minWidth: 'max-content',
                  pt: 1,
                }}
              >
                <TextField
                  size="small"
                  label="Search Accessions"
                  placeholder="Search barcodes, patient, provider, practice, workflow"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  sx={{ minWidth: 320, flex: '1 1 320px' }}
                  InputProps={{
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 160, flex: '0 0 auto' }}>
                  <InputLabel id="accession-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="accession-status-filter-label"
                    value={statusFilter}
                    label="Status"
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170, flex: '0 0 auto' }}>
                  <InputLabel id="accession-barcode-match-filter-label">Barcode Match</InputLabel>
                  <Select
                    labelId="accession-barcode-match-filter-label"
                    value={barcodeMatchFilter}
                    label="Barcode Match"
                    onChange={(event) => {
                      setBarcodeMatchFilter(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="matched">Matched</MenuItem>
                    <MenuItem value="mismatched">Mismatched</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160, flex: '0 0 auto' }}>
                  <InputLabel id="accession-case-filter-label">Case</InputLabel>
                  <Select
                    labelId="accession-case-filter-label"
                    value={hasCaseFilter}
                    label="Case"
                    onChange={(event) => {
                      setHasCaseFilter(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="with_case">With Case</MenuItem>
                    <MenuItem value="without_case">No Case</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <TableContainer component={Paper}>
              {loading ? (
                <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Container>
              ) : error ? (
                <Container disableGutters sx={{ py: 3 }}>
                  <Typography color="error">{error}</Typography>
                </Container>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sample ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No accessions found for this lab.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => navigate(`/lab-accessioning/${row.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              navigate(`/lab-accessioning/${row.id}`);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{row.sample_id || 'None'}</TableCell>
                          <TableCell>{row.status || 'Unknown'}</TableCell>
                          <TableCell>{formatCreatorName(row)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {row.created_at ? new Date(row.created_at).toLocaleString() : 'Unknown'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </CardContent>
        </Card>
      )}

      <Dialog
        open={newAccessionDialogOpen}
        onClose={handleCloseNewAccessionDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: { md: 720 },
          },
        }}
      >
        <DialogTitle>New Accession</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.5, md: 2 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Card
              sx={{
                border: '1px solid #d8e2e7',
                boxShadow: 'none',
                borderRadius: 2,
                width: { xs: '100%', md: 216 },
                flexShrink: 0,
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, md: 1.75 } }}>
                <Stepper activeStep={newAccessionStep} orientation="vertical">
                  {accessionWizardSteps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>

            <Card
              sx={{
                border: '1px solid #d8e2e7',
                boxShadow: 'none',
                borderRadius: 2,
                flex: 1,
                width: '100%',
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, md: 1.75 } }}>
                {newAccessionStep === 0 ? (
                  <Stack spacing={1.5}>
                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.25 }}>
                        <Stack spacing={0.875}>
                          <Typography variant="subtitle2" color="text.secondary">
                            1a. Scan Sample
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Scan the barcode on the requisition form and the barcode on the sample container. The two
                            values must match before you can continue.
                          </Typography>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ alignItems: { xs: 'flex-start', md: 'flex-end' } }}
                          >
                            <Stack spacing={1.25} sx={{ width: '100%', maxWidth: 560 }}>
                              <TextField
                                autoFocus
                                label="Requisition Form Barcode"
                                value={newAccessionRequisitionBarcode}
                                onChange={(event) => {
                                  setNewAccessionRequisitionBarcode(event.target.value);
                                  setAdvanceStepError(null);
                                }}
                                placeholder="Scan or type requisition barcode"
                                fullWidth
                              />
                              <TextField
                                label="Sample Barcode"
                                value={newAccessionSampleBarcode}
                                onChange={(event) => {
                                  setNewAccessionSampleBarcode(event.target.value);
                                  setAdvanceStepError(null);
                                }}
                                placeholder="Scan or type sample barcode"
                                fullWidth
                              />
                            </Stack>
                            <TwoDBarcodePlaceholder />
                          </Stack>
                          {hasBothWizardScans && !wizardBarcodesMatch && (
                            <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                              Barcodes do not match. Verify both scans before continuing.
                            </Typography>
                          )}
                          {wizardBarcodesMatch && (
                            <Typography variant="caption" sx={{ color: '#166534' }}>
                              Barcodes match. Select the test SKU for sample ID <strong>{wizardSampleId}</strong> to continue.
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2, width: '100%' }}>
                      <CardContent sx={{ p: 1.25 }}>
                        <Stack spacing={0.875} sx={{ width: '100%', maxWidth: 560 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            1b. Select Test SKU
                          </Typography>
                          <TextField
                            label="Search SKU by Name or Number"
                            value={skuSearchInput}
                            onChange={(event) => {
                              setSkuSearchInput(event.target.value);
                              setAdvanceStepError(null);
                            }}
                            onFocus={(event) => setSkuSearchAnchorEl(event.currentTarget)}
                            fullWidth
                            size="small"
                            disabled={!wizardBarcodesMatch}
                            helperText={wizardBarcodesMatch ? ' ' : 'Match the sample and requisition barcodes first.'}
                          />
                          {advanceStepError && (
                            <Typography variant="body2" color="error">
                              {advanceStepError}
                            </Typography>
                          )}
                          <Card
                            sx={{
                              border: '1px solid #dbe3e8',
                              boxShadow: 'none',
                              borderRadius: 2,
                              bgcolor: selectedSkuOption ? '#ffffff' : '#f8fafc',
                              opacity: selectedSkuOption ? 1 : 0.8,
                            }}
                          >
                            <CardContent sx={{ p: 1 }}>
                              <Stack spacing={0.25}>
                                <Typography variant="body2">
                                  <strong>Name:</strong> {selectedSkuOption?.name || ''}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>SKU:</strong> {selectedSkuOption?.sku_number || ''}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Workflow:</strong> {selectedSkuOption?.workflow_name || ''}
                                </Typography>
                              </Stack>
                            </CardContent>
                          </Card>
                          {selectedSkuOption && !selectedSkuOption.lab_test_workflow_id && (
                            <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                              This SKU does not have a workflow assigned and cannot be accessioned yet.
                            </Typography>
                          )}
                          <Popover
                            open={skuPopoverOpen}
                            anchorEl={skuSearchAnchorEl}
                            onClose={() => setSkuSearchAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                              sx: {
                                mt: 1,
                                width: skuSearchAnchorEl ? Math.max(skuSearchAnchorEl.clientWidth, 360) : 360,
                                maxHeight: 320,
                                overflowY: 'auto',
                                p: 1,
                              },
                            }}
                          >
                            {skuSearchLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : skuSearchError ? (
                              <Typography variant="body2" color="error" sx={{ p: 1 }}>
                                {skuSearchError}
                              </Typography>
                            ) : skuSearchResults.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                No lab test SKUs found.
                              </Typography>
                            ) : (
                              <Stack spacing={1}>
                                {skuSearchResults.map((sku) => (
                                  <Card
                                    key={sku.id}
                                    sx={{
                                      border: '1px solid #e2e8f0',
                                      boxShadow: 'none',
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectSkuOption(sku)}
                                  >
                                    <CardContent sx={{ p: 1.25 }}>
                                      <Typography variant="subtitle2">{sku.name}</Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        SKU: {sku.sku_number}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Workflow: {sku.workflow_name || 'No workflow'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Stack>
                            )}
                          </Popover>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                ) : newAccessionStep === 1 ? (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" component="h2">
                      2. Enter Order Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter the patient and provider details from the requisition form for sample ID <strong>{wizardSampleId}</strong>.
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="flex-start">
                      <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2, flex: 1 }}>
                        <CardContent sx={{ p: 1.25 }}>
                          <Stack spacing={0.875}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Patient
                            </Typography>
                            <TextField
                              label="First Name"
                              value={newAccessionOrderDetails.patient_first_name}
                              onChange={(event) =>
                                handleNewAccessionOrderDetailChange('patient_first_name', event.target.value)
                              }
                              fullWidth
                              size="small"
                            />
                            <TextField
                              label="Last Name"
                              value={newAccessionOrderDetails.patient_last_name}
                              onChange={(event) =>
                                handleNewAccessionOrderDetailChange('patient_last_name', event.target.value)
                              }
                              fullWidth
                              size="small"
                            />
                            <TextField
                              label="DOB"
                              value={newAccessionOrderDetails.patient_dob}
                              onChange={(event) =>
                                handleNewAccessionOrderDetailChange('patient_dob', event.target.value)
                              }
                              onBlur={() => setNewAccessionDobTouched(true)}
                              placeholder="YYYY-MM-DD"
                              fullWidth
                              size="small"
                              error={Boolean(newAccessionDobTouched && newAccessionOrderDetails.patient_dob && dobError)}
                              helperText={
                                newAccessionDobTouched && newAccessionOrderDetails.patient_dob
                                  ? dobError || ' '
                                  : ' '
                              }
                            />
                          </Stack>
                        </CardContent>
                      </Card>

                      <Stack spacing={1} sx={{ flex: 1 }}>
                        <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.25 }}>
                            <Stack spacing={0.875}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Practice
                              </Typography>
                              <TextField
                                label="Search Practice by Name or NPI"
                                value={practiceSearchInput}
                                onChange={(event) => setPracticeSearchInput(event.target.value)}
                                onFocus={(event) => setPracticeSearchAnchorEl(event.currentTarget)}
                                fullWidth
                                size="small"
                              />
                              <Card
                                sx={{
                                  border: '1px solid #dbe3e8',
                                  boxShadow: 'none',
                                  borderRadius: 2,
                                  bgcolor: selectedPracticeOption ? '#ffffff' : '#f8fafc',
                                  opacity: selectedPracticeOption ? 1 : 0.8,
                                }}
                              >
                                <CardContent sx={{ p: 1 }}>
                                  <Stack spacing={0.25}>
                                    <Typography variant="body2">
                                      <strong>Name:</strong> {selectedPracticeOption?.name || ''}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>NPI:</strong> {selectedPracticeOption?.npi || ''}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Address:</strong> {selectedPracticeOption ? formatPracticeAddress(selectedPracticeOption) : ''}
                                    </Typography>
                                  </Stack>
                                </CardContent>
                              </Card>
                              <Popover
                                open={practicePopoverOpen}
                                anchorEl={practiceSearchAnchorEl}
                                onClose={() => setPracticeSearchAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                PaperProps={{
                                  sx: {
                                    mt: 1,
                                    width: practiceSearchAnchorEl ? Math.max(practiceSearchAnchorEl.clientWidth, 360) : 360,
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    p: 1,
                                  },
                                }}
                              >
                                {practiceSearchLoading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : practiceSearchError ? (
                                  <Typography variant="body2" color="error" sx={{ p: 1 }}>
                                    {practiceSearchError}
                                  </Typography>
                                ) : practiceSearchResults.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                    No practices found.
                                  </Typography>
                                ) : (
                                  <Stack spacing={1}>
                                    {practiceSearchResults.map((practice) => (
                                      <Card
                                        key={practice.id}
                                        sx={{
                                          border: '1px solid #e2e8f0',
                                          boxShadow: 'none',
                                          borderRadius: 2,
                                          cursor: 'pointer',
                                        }}
                                        onClick={() => handleSelectPracticeOption(practice)}
                                      >
                                        <CardContent sx={{ p: 1.25 }}>
                                          <Typography variant="subtitle2">{practice.name}</Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            NPI: {practice.npi || 'Unknown'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {formatPracticeAddress(practice)}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </Stack>
                                )}
                              </Popover>
                            </Stack>
                          </CardContent>
                        </Card>

                        <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.25 }}>
                            <Stack spacing={0.875}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Provider
                              </Typography>
                              {!selectedPracticeOption ? (
                                <Typography variant="body2" color="text.secondary">
                                  Select a practice first to list its providers.
                                </Typography>
                              ) : (
                                <>
                                  <TextField
                                    label="Filter Providers"
                                    value={providerSearchInput}
                                    onChange={(event) => setProviderSearchInput(event.target.value)}
                                    onFocus={(event) => setProviderSearchAnchorEl(event.currentTarget)}
                                    fullWidth
                                    size="small"
                                  />
                                  <Card
                                    sx={{
                                      border: '1px solid #dbe3e8',
                                      boxShadow: 'none',
                                      borderRadius: 2,
                                      bgcolor: selectedProviderOption ? '#ffffff' : '#f8fafc',
                                      opacity: selectedProviderOption ? 1 : 0.8,
                                    }}
                                  >
                                    <CardContent sx={{ p: 1 }}>
                                      <Stack spacing={0.25}>
                                        <Typography variant="body2">
                                          <strong>Name:</strong> {[selectedProviderOption?.first_name, selectedProviderOption?.last_name].filter(Boolean).join(' ')}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>NPI:</strong> {selectedProviderOption?.npi || ''}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Email:</strong> {selectedProviderOption?.email || ''}
                                        </Typography>
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                  <Popover
                                    open={providerPopoverOpen}
                                    anchorEl={providerSearchAnchorEl}
                                    onClose={() => setProviderSearchAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    PaperProps={{
                                      sx: {
                                        mt: 1,
                                        width: providerSearchAnchorEl ? Math.max(providerSearchAnchorEl.clientWidth, 360) : 360,
                                        maxHeight: 320,
                                        overflowY: 'auto',
                                        p: 1,
                                      },
                                    }}
                                  >
                                    {providerSearchLoading ? (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : providerSearchError ? (
                                      <Typography variant="body2" color="error" sx={{ p: 1 }}>
                                        {providerSearchError}
                                      </Typography>
                                    ) : providerSearchResults.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                        No providers found for the selected practice.
                                      </Typography>
                                    ) : (
                                      <Stack spacing={1}>
                                        {providerSearchResults.map((provider) => (
                                          <Card
                                            key={provider.user_id}
                                            sx={{
                                              border: '1px solid #e2e8f0',
                                              boxShadow: 'none',
                                              borderRadius: 2,
                                              cursor: 'pointer',
                                            }}
                                            onClick={() => handleSelectProviderOption(provider)}
                                          >
                                            <CardContent sx={{ p: 1.25 }}>
                                              <Typography variant="subtitle2">
                                                {[provider.first_name, provider.last_name].filter(Boolean).join(' ') || 'Unknown'}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                NPI: {provider.npi || 'Unknown'}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                {provider.email || 'No email'}
                                              </Typography>
                                            </CardContent>
                                          </Card>
                                        ))}
                                      </Stack>
                                    )}
                                  </Popover>
                                </>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Stack>
                    </Stack>
                  </Stack>
                ) : newAccessionStep === 2 ? (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" component="h2">
                      3. Confirm Order
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check that these details match the requisition form.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      Sample ID: <strong>{wizardSampleId}</strong>
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="flex-start">
                      <Stack spacing={1} sx={{ flex: 1 }}>
                        <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.25 }}>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Patient
                              </Typography>
                              <Typography variant="body2">
                                <strong>First Name:</strong> {newAccessionOrderDetails.patient_first_name || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Last Name:</strong> {newAccessionOrderDetails.patient_last_name || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>DOB:</strong> {newAccessionOrderDetails.patient_dob || 'Unknown'}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>

                        <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.25 }}>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Test SKU
                              </Typography>
                              <Typography variant="body2">
                                <strong>Name:</strong> {selectedSkuOption?.name || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>SKU:</strong> {newAccessionOrderDetails.sku_number || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Workflow:</strong> {selectedSkuOption?.workflow_name || 'No workflow'}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Stack>

                      <Stack spacing={1} sx={{ flex: 1 }}>
                        <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.25 }}>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Practice
                              </Typography>
                              <Typography variant="body2">
                                <strong>Name:</strong> {selectedPracticeOption?.name || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>NPI:</strong> {selectedPracticeOption?.npi || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Address:</strong> {formatPracticeAddress(selectedPracticeOption)}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>

                        <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.25 }}>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Provider
                              </Typography>
                              <Typography variant="body2">
                                <strong>Name:</strong> {[selectedProviderOption?.first_name, selectedProviderOption?.last_name].filter(Boolean).join(' ') || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>NPI:</strong> {selectedProviderOption?.npi || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Email:</strong> {selectedProviderOption?.email || 'Unknown'}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Stack>
                    </Stack>
                  </Stack>
                ) : newAccessionStep === 3 ? (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" component="h2">
                      4. Inspect Sample
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Answer the accessioning questions for the selected workflow before continuing.
                    </Typography>
                    {saveAccessionError && (
                      <Typography variant="body2" color="error">
                        {saveAccessionError}
                      </Typography>
                    )}

                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.25 }}>
                        <Stack spacing={1.25}>
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            Workflow: <strong>{selectedSkuOption?.workflow_name || 'No workflow'}</strong>
                          </Typography>
                          {workflowQuestionsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : workflowQuestionsError ? (
                            <Typography variant="body2" color="error">
                              {workflowQuestionsError}
                            </Typography>
                          ) : !selectedSkuOption?.lab_test_workflow_id ? (
                            <Typography variant="body2" color="text.secondary">
                              The selected SKU does not have a workflow assigned.
                            </Typography>
                          ) : workflowQuestions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No active accessioning questions were found for this workflow.
                            </Typography>
                          ) : (
                            <Stack spacing={1.25}>
                              {workflowQuestions.map((question) => (
                                <Stack
                                  key={question.id}
                                  direction={{ xs: 'column', md: 'row' }}
                                  spacing={1.5}
                                  alignItems={{ xs: 'flex-start', md: 'center' }}
                                >
                                  <ToggleButtonGroup
                                    exclusive
                                    value={workflowQuestionAnswers[question.id] || 'na'}
                                    onChange={(_, value) => {
                                      setWorkflowQuestionAnswers((current) => ({
                                        ...current,
                                        [question.id]: value ?? current[question.id] ?? 'na',
                                      }));
                                    }}
                                    size="small"
                                    sx={segmentGroupSx}
                                  >
                                    <ToggleButton
                                      value="no"
                                      sx={{
                                        '&.Mui-selected': {
                                          bgcolor: 'rgba(220, 38, 38, 0.14)',
                                          color: '#991b1b',
                                        },
                                      }}
                                    >
                                      No
                                    </ToggleButton>
                                    <ToggleButton value="na">-</ToggleButton>
                                    <ToggleButton
                                      value="yes"
                                      sx={{
                                        '&.Mui-selected': {
                                          bgcolor: 'rgba(22, 163, 74, 0.16)',
                                          color: '#166534',
                                        },
                                      }}
                                    >
                                      Yes
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2">{question.question_text}</Typography>
                                  </Box>
                                </Stack>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                    {showRejectSampleButton && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setRejectSampleDialogOpen(true)}
                        disabled={saveAccessionLoading}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Reject Sample, and Send Notification
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" component="h2">
                      5. Final Directions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review the accession details, then save to add the accession to the lab queue.
                    </Typography>

                    {saveAccessionError && (
                      <Typography variant="body2" color="error">
                        {saveAccessionError}
                      </Typography>
                    )}

                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.25 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Summary
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                              gap: 1.25,
                            }}
                          >
                            <Stack spacing={0.35}>
                              <Typography variant="caption"><strong>Sample ID:</strong> {wizardSampleId || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>Requisition Barcode:</strong> {newAccessionRequisitionBarcode || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>Sample Barcode:</strong> {newAccessionSampleBarcode || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>SKU:</strong> {selectedSkuOption?.sku_number || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>Workflow:</strong> {selectedSkuOption?.workflow_name || 'Unknown'}</Typography>
                            </Stack>
                            <Stack spacing={0.35}>
                              <Typography variant="caption"><strong>Patient:</strong> {[newAccessionOrderDetails.patient_first_name, newAccessionOrderDetails.patient_last_name].filter(Boolean).join(' ') || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>DOB:</strong> {newAccessionOrderDetails.patient_dob || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>Practice:</strong> {selectedPracticeOption?.name || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>Provider:</strong> {[selectedProviderOption?.first_name, selectedProviderOption?.last_name].filter(Boolean).join(' ') || 'Unknown'}</Typography>
                              <Typography variant="caption"><strong>Practice NPI:</strong> {selectedPracticeOption?.npi || 'Unknown'}</Typography>
                            </Stack>
                          </Box>
                          <Stack spacing={0.35}>
                            <Typography variant="caption" color="text.secondary">
                              Inspection Answers
                            </Typography>
                            {workflowQuestionSummaries.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No workflow questions answered.
                              </Typography>
                            ) : (
                              workflowQuestionSummaries.map((item) => (
                                <Typography key={item.id} variant="caption">
                                  <strong>{item.answer}:</strong> {item.question_text}
                                </Typography>
                              ))
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.25 }}>
                        <Stack spacing={0.75}>
                          <Typography variant="subtitle2">Next Step</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum imperdiet, urna at
                            ullamcorper congue, nisl sapien posuere lectus, in tincidunt sem lorem nec velit.
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewAccessionDialog}>
            Cancel
          </Button>
          {newAccessionStep > 0 && (
            <Button onClick={() => setNewAccessionStep((value) => value - 1)}>
              Back
            </Button>
          )}
          {newAccessionStep < accessionWizardSteps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleContinueAccessionWizard}
              disabled={
                advanceStepLoading ||
                (newAccessionStep === 0 && (!wizardBarcodesMatch || !selectedSkuOption?.lab_test_workflow_id)) ||
                (newAccessionStep === 1 && !wizardHasOrderDetails) ||
                (newAccessionStep === 3 && !workflowQuestionsAllYes)
              }
            >
              {advanceStepLoading ? 'Validating...' : 'Continue'}
            </Button>
          )}
          {newAccessionStep === accessionWizardSteps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleSaveAccession}
              disabled={saveAccessionLoading || !selectedSkuOption?.lab_test_workflow_id}
            >
              {saveAccessionLoading ? 'Saving...' : 'Done'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={rejectSampleDialogOpen}
        onClose={() => {
          if (!saveAccessionLoading) {
            setRejectSampleDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Sample</DialogTitle>
        <DialogContent dividers>
          {saveAccessionLoading ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" align="center">
                Rejecting sample and sending notification...
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {`Reject this sample because:\n${negativeWorkflowQuestions.map((question) => question.summary).join('\n')}`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectSampleDialogOpen(false)} disabled={saveAccessionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRejectSample}
            disabled={saveAccessionLoading}
          >
            {saveAccessionLoading ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
